import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ExternalLink } from 'lucide-react';
import { Container } from '../layout/Primitives';
import StarRating from './StarRating';
import StorefrontReviewRows from './StorefrontReviewRows';
import { getReviewsOverview } from '../../services/reviewService';
import { isApiEnabled } from '../../config/env';

const Section = styled.section`
  padding: clamp(3rem, 6vw, 5.5rem) 0;
  background: hsla(24, 30%, 94%, 0.42);
`;

const TopRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    gap: 2rem;
  }
`;

const Kicker = styled.p`
  margin: 0 0 0.35rem;
  font-size: ${({ theme }) => theme.type.bodyXs};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-family: ${({ theme }) => theme.fonts.sans};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.display};
  font-weight: 300;
  font-size: clamp(1.75rem, 3vw + 1rem, 2.65rem);
  line-height: 1.15;
`;

const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(0.5rem, 1.5vw, 0.85rem);
  margin-top: clamp(0.65rem, 1.2vw, 0.85rem);

  span strong {
    font-weight: 500;
    font-size: ${({ theme }) => theme.type.bodySm};
    font-family: ${({ theme }) => theme.fonts.sans};
  }

  span .muted {
    font-size: ${({ theme }) => theme.type.bodyXs};
    font-family: ${({ theme }) => theme.fonts.sans};
    color: ${({ theme }) => theme.colors.mutedForeground};
  }
`;

const BtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
`;

const BtnPri = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.65rem 1.15rem;
  font-size: ${({ theme }) => theme.type.bodyXs};
  font-family: ${({ theme }) => theme.fonts.sans};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-decoration: none;
  background: ${({ theme }) => theme.colors.foreground};
  color: ${({ theme }) => theme.colors.background};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.88;
  }
`;

const BtnOut = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.65rem 1.05rem;
  font-size: ${({ theme }) => theme.type.bodyXs};
  font-family: ${({ theme }) => theme.fonts.sans};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border: 1px solid ${({ theme }) => theme.colors.foreground};
  background: transparent;
  color: ${({ theme }) => theme.colors.foreground};
  text-decoration: none;

  &:hover {
    background: hsla(20, 10%, 10%, 0.04);
  }
`;

export default function HomeReviewsSection() {
  const { t } = useTranslation();
  const useApi = isApiEnabled();
  const trustpilotUrl = (process.env.REACT_APP_TRUSTPILOT_REVIEWS_URL || '').trim();
  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!useApi) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await getReviewsOverview({ limit: 15 });
        if (!cancelled) {
          setSummary(data.summary);
          setReviews(data.reviews);
        }
      } catch {
        if (!cancelled) {
          setSummary(null);
          setReviews([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [useApi]);

  if (!useApi || loading) return null;
  if (!summary?.count) return null;

  const avg = Number(summary.average) || 0;

  return (
    <Section aria-labelledby="home-reviews-title">
      <Container>
        <TopRow>
          <div>
            <Kicker>{t('modamai.homeReviews.kicker')}</Kicker>
            <Title id="home-reviews-title">{t('modamai.homeReviews.title')}</Title>
            <RatingRow>
              <StarRating rating={avg} size="lg" />
              <span>
                <strong>{avg.toFixed(1)}</strong>
                <span className="muted"> {t('modamai.homeReviews.fromReviews', { count: summary.count })}</span>
              </span>
            </RatingRow>
          </div>
          <BtnRow>
            <BtnPri to="/reviews">{t('modamai.homeReviews.seeAll')}</BtnPri>
            {trustpilotUrl ? (
              <BtnOut href={trustpilotUrl} target="_blank" rel="noopener noreferrer">
                <span
                  aria-hidden
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    background: 'hsl(140 70% 45%)',
                    borderRadius: 2,
                  }}
                />
                {t('modamai.homeReviews.trustpilot')}
                <ExternalLink size={14} aria-hidden />
              </BtnOut>
            ) : null}
          </BtnRow>
        </TopRow>
        <StorefrontReviewRows reviews={reviews || []} />
      </Container>
    </Section>
  );
}
